import React, { useEffect, useState } from "react";
import { DialogBox } from "../../shared/components/dialog";
import { Box, DialogContent, FormControl, FormHelperText, IconButton, InputAdornment, InputLabel, LinearProgress, OutlinedInput, Stack, TextField } from "@mui/material";
import { changePassword, getUserInfo } from "../../redux/features/user.slice";
import { useDispatch, useSelector } from "react-redux";
import { RootStateType } from "../../redux/features/rootSlice";
import { calculatePasswordStrength, passwordRegex } from "../../shared/util/authentication.regex";
import { MESSAGES, webSocketMessage } from "../../config/app";
import EyeCloseIcon from "../../shared/icon/eye-close-icon";
import EyeOpenIcon from "../../shared/icon/eye-open-icon";
import useWebSocket from "../../hooks/useWebSocket";
import { useArcAuth } from "@arcauth/react-sdk";
import { grey, red } from "@mui/material/colors";
import { useNavigate } from "react-router-dom";
export interface DialogProps {
  open: boolean;
  onClose: () => void;
}
interface password {
  oldpassword: string;
  newpassword: string;
  confirmpassword: string;
}
const PasswordUpdate = (props: DialogProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { logout } = useArcAuth();
  const { open, onClose } = props;
  const { updateUserAndNotifyParent } = useWebSocket();
  const [password, setPassword] = useState<password>({ oldpassword: "", newpassword: "", confirmpassword: "" });
  const [buttonEnabled, setButtonEnabled] = useState(false);
  const [isOldPasswordTouched, setIsOldPasswordTouched] = useState(false);
  const [oldPasswordHelperText, setOldPasswordHelperText] = useState("");
  const [isNewPasswordTouched, setIsNewPasswordTouched] = useState(false);
  const [newPasswordHelperText, setNewPasswordHelperText] = useState("");
  const [isConfirmPasswordTouched, setIsConfirmPasswordTouched] = useState(false);
  const [confirmPasswordHelperText, setConfirmPasswordHelperText] = useState("");
  const [strength, setStrength] = useState(0);
  const [strengthLabel, setStrengthLabel] = useState('');

  const passwordUpdate_loading = useSelector(
    (state: RootStateType) => state.userStateReducer.loading
  );
  const storedClientId = useSelector((state: RootStateType) => state.configStateReducer.clientId);
  const clientType = useSelector((state: RootStateType) => state.deviceConfigReducer.clientType);
  const handleListItemClick = async (password: any) => {
    // if (password.newpassword != password.confirmpassword) {
    //   console.log("new password and confirm password does not match");
    // }
    const dispatchObj = {
      password: {
        new_password: password.newpassword,
        old_password: password.oldpassword,
      },
      callBackSuccess: () => {
        updateUserAndNotifyParent(webSocketMessage.passwordResponse);
        dialogClose();
        dispatch(getUserInfo());
        setButtonEnabled(false);
        setShowPassword(false);
        /**
          * NOTE: on uncommenting this user will be logout from current browser
          * after reseting password with old password.
        */ 
        // localStorage.clear();
        // if (clientType && clientType == 'device') {
        //   // navigate("/error");
        // // Trigger the custom protocol
        // window.location.href = 'arcauth.facilities:/oauthredirect';
        // }
        // else {
        //   logout();
        // }
      },
      successMessage: MESSAGES.passwordSuccess,
      callBackFailure: () => {
        setPassword({
          oldpassword: "",
          newpassword: "",
          confirmpassword: ""
        });
        setShowPassword(false);
      },
    };
    dispatch(changePassword(dispatchObj));
  };

  const checkOldPassword = (oldpassword: any) => {
    setIsOldPasswordTouched(true);

    if (oldpassword.length < 1) {
      setOldPasswordHelperText("Old password cannot be empty");
    } else {
      setOldPasswordHelperText("");
    }
  };


  const checkNewPassword = (newpassword: string, password: any) => {
    setIsNewPasswordTouched(true);
  
    if (newpassword.length < 1) {
      setStrength(0);
      setStrengthLabel('');
      setNewPasswordHelperText("");
      return;
    }
  
    const strengthCount = calculatePasswordStrength(newpassword);
  
    let calculatedStrength = 0;
    let calculatedLabel = '';
  
    if (strengthCount === 4) {
      calculatedStrength = newpassword.length > 8 ? 100 : 80;
      calculatedLabel = newpassword.length > 8 ? 'Strong' : 'Good';
    } else if (strengthCount === 3) {
      calculatedStrength = 60;
      calculatedLabel = 'Weak';
    } else if (strengthCount === 2) {
      calculatedStrength = 40;
      calculatedLabel = 'Poor';
    } else {
      calculatedStrength = 20;
      calculatedLabel = 'Poor';
    }
  
    setStrength(calculatedStrength);
    setStrengthLabel(calculatedLabel);
    setNewPasswordHelperText("");
    
    if (newpassword === password.confirmpassword) {
      setConfirmPasswordHelperText(""); // Reset confirm password helper text
    }
  };



  const checkConfirmPassword = (confirmpassword: any, password: any) => {
    setIsConfirmPasswordTouched(true);
    if (isConfirmPasswordTouched && confirmpassword.length < 1) {
      setConfirmPasswordHelperText("");
    } else if (password.newpassword !== confirmpassword) {
      setConfirmPasswordHelperText("New password and confirm password does not match");
    } else {
      setConfirmPasswordHelperText("");
    }
  };

  const clearForm = () => {
    setIsOldPasswordTouched(false);
    setOldPasswordHelperText("");
    setIsNewPasswordTouched(false);
    setNewPasswordHelperText("");
    setIsConfirmPasswordTouched(false);
    setConfirmPasswordHelperText("");

    setPassword({
      oldpassword: "",
      newpassword: "",
      confirmpassword: ""
    });
  };
  const dialogClose = () => {
    onClose();
    clearForm();
    setShowPassword(false);
    setStrength(0);
    setStrengthLabel('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && buttonEnabled) {
      e.preventDefault();
      handleListItemClick(password);
    }
  };

  useEffect(() => {
    // Check if any of the passwords is empty or if confirm password is weak
    if (
      password.oldpassword === "" ||
      password.newpassword === "" ||
      password.confirmpassword === "" ||
      !passwordRegex.test(password.confirmpassword) ||
      password.newpassword !== password.confirmpassword // Check if new password doesn't match confirm password
    ) {
      // If any of the conditions above is true, disable the button
      setButtonEnabled(false);
    } else {
      // If all passwords are filled and confirm password is not weak, enable the button
      setButtonEnabled(true);
    }
  }, [password]);

  const [showPassword, setShowPassword] = React.useState(false);
  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };
  
// Function to get the appropriate MUI color based on strength
const getProgressColor = (strength: number): 'primary' | 'secondary' | 'error' | 'warning' | 'success' => {
  return strength >= 80 ? 'success' : 
         strength >= 60 ? 'warning' : 
         strength >= 40 ? 'secondary' :  // We'll apply custom light red styling via sx
         strength >= 20 ? 'error' : 'secondary';
};
  
  return (
    <>
      <DialogBox
        className="password-name"
        onClose={dialogClose}
        title={"Change password"}
        buttonText={"Update"}
        buttonClick={() => {
          handleListItemClick(password);
        }}
        isLoading={passwordUpdate_loading}
        isBtnEnabled={!buttonEnabled}
        open={open}
        style={{
          maxWidth: 500,
          mx: "auto",
        }}
        buttonAriaLabel="Click to update your password"
      >
        <DialogContent>
          <Stack>
            <div>
              <label htmlFor="old-password" style={{ fontSize: 13 }}>
                Old password
              </label>
              <TextField
                id="old-password"
                size="small"
                fullWidth
                placeholder="Enter old password"
                autoFocus
                inputProps={{ maxLength: 100,'aria-describedby': 'old-password-helper-text'  }}
                
                value={password.oldpassword}
                type="password"
                color="secondary"
                onKeyDown={handleKeyDown}
                onChange={(e) => {
                  checkOldPassword(e.target.value);
                  setPassword({
                    newpassword: password.newpassword,
                    confirmpassword: password.confirmpassword,
                    oldpassword: e.target.value,
                  });
                }}
              />
              <div
                id="old-password-helper-text"
                style={{
                  position: 'absolute',
                  left: '-9999px',
                  width: '1px',
                  height: '1px',
                  overflow: 'hidden',
                }}
              >
                Password requirements: Use at least 8 characters. Include both lowercase
                and uppercase letters. Include at least one digit. Include at least one
                symbol (!@#$%^&*_-+=). Avoid using your name or email in the password.
              </div>
              <FormHelperText
                style={{
                  color: "red",
                }}
              >
                {oldPasswordHelperText}
              </FormHelperText>
            </div>
            <Box>
              <FormControl fullWidth size="small" color="secondary">
                <label htmlFor="new-password" style={{ fontSize: 13 }}>
                  New password
                </label>
                <OutlinedInput
                  id="new-password"
                  type={showPassword ? "text" : "password"}
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                      >
                        {showPassword ? <EyeOpenIcon /> : <EyeCloseIcon />}
                      </IconButton>
                    </InputAdornment>
                  }
                  //label="Password"
                  size="small"
                  fullWidth
                  placeholder="Enter new password"
                  color="secondary"
                  inputProps={{ maxLength: 100,'aria-describedby': 'password-instructions' }}
                  value={password.newpassword}
                  onChange={(e) => {
                    checkNewPassword(e.target.value, password);
                    setPassword({
                      oldpassword: password.oldpassword,
                      confirmpassword: password.confirmpassword,
                      newpassword: e.target.value,
                    });
                  }}
                />
                <div
                  id="password-instructions"
                  style={{
                    position: 'absolute',
                    left: '-9999px',
                    width: '1px',
                    height: '1px',
                    overflow: 'hidden',
                  }}
                >
                  Password requirements: Use at least 8 characters. Include both lowercase
                  and uppercase letters. Include at least one digit. Include at least one
                  symbol (!@#$%^&*_-+=). Avoid using your name or email in the password.
                </div>
                <FormHelperText
                  style={{
                    color: "red",
                  }}
                >
                  {newPasswordHelperText}
                </FormHelperText>
              </FormControl>
            </Box>
            {/* <div>
              <TextField size="small" fullWidth label="New password" type="password" color="secondary" inputProps={{ maxLength: 100 }} value={password.newpassword}
                onChange={(e) => {
                  checkNewPassword(e.target.value, password);
                  setPassword({ oldpassword: password.oldpassword, confirmpassword: password.confirmpassword, newpassword: e.target.value });
                }} />
              <FormHelperText
                style={{
                  color: "red",
                }}
              >
                {newPasswordHelperText}
              </FormHelperText>
            </div> */}
            <div>
              <label htmlFor="confirm-password" style={{ fontSize: 13 }}>
                Confirm password
              </label>
              <TextField
                id="confirm-password"
                size="small"
                fullWidth
                placeholder="Enter confirm password"
                type="password"
                color="secondary"
                inputProps={{ maxLength: 100,'aria-describedby': 'confirm-password-helper-text'  }}
                value={password.confirmpassword}
                onKeyDown={handleKeyDown}
                onChange={(e) => {
                  checkConfirmPassword(e.target.value, password);
                  setPassword({
                    oldpassword: password.oldpassword,
                    newpassword: password.newpassword,
                    confirmpassword: e.target.value,
                  });
                }}
                onPaste={(e) => {
                  e.preventDefault(); // Prevent pasting
                }}
              />
              <div
                id="confirm-password-helper-text"
                style={{
                  position: 'absolute',
                  left: '-9999px',
                  width: '1px',
                  height: '1px',
                  overflow: 'hidden',
                }}
              >
                Password requirements: Use at least 8 characters. Include both lowercase
                and uppercase letters. Include at least one digit. Include at least one
                symbol (!@#$%^&*_-+=). Avoid using your name or email in the password.
              </div>
              <FormHelperText
                style={{
                  color: "red",
                }}
              >
                {confirmPasswordHelperText}
              </FormHelperText>
            </div>
          </Stack>
        </DialogContent>
        <Box className="status-container">
          <Box className="status">
            <div style={{ fontSize: "1rem", fontWeight: 600 }}>
              <strong>Password Strength: </strong>
              <span id="strength">{strengthLabel}</span>
            </div>

            <Box className="progress-bar">
              <LinearProgress
                variant="determinate"
                value={strength}
                color={getProgressColor(strength)}
                sx={{
                  ...(strength >= 40 &&
                    strength < 60 && {
                      "& .MuiLinearProgress-bar": {
                        backgroundColor: red[300],
                      },
                    }),
                }}
              />
            </Box>

            <ul>
              <li>Use at least 8 characters.</li>
              <li>Include both lowercase and uppercase letters.</li>
              <li>Include at least one digit.</li>
              <li>Include at least one symbol (!@#$%^&amp;*_-+=).</li>
              <li>Avoid using your name or email in the password.</li>
            </ul>
          </Box>
        </Box>
      </DialogBox>
    </>
  );
};

export default PasswordUpdate;
